import { MediaType } from "@/stores/media-store";
import { generateUUID } from "@/lib/utils";
import { FilterSettings } from "./filters";
import { EffectSettings } from "./effects";
import { AdjustmentSettings } from "./adjustments";
import { AudioSettings } from "./audio";
import { TransitionSettings } from "./transitions";

export type TrackType = "media" | "text" | "audio" | "caption" | "sticker";

// Base element properties
interface BaseTimelineElement {
  id: string;
  name: string;
  duration: number;
  startTime: number;
  trimStart: number;
  trimEnd: number;
}

// Media element that references MediaStore
export interface MediaElement extends BaseTimelineElement {
  type: "media";
  mediaId: string;
  filters?: Partial<FilterSettings>;
  effects?: Partial<EffectSettings>;
  adjustments?: Partial<AdjustmentSettings>;
  audio?: Partial<AudioSettings>;
}

// Text element with embedded text data
export interface TextElement extends BaseTimelineElement {
  type: "text";
  content: string;
  fontSize: number;
  fontFamily: string;
  color: string;
  backgroundColor: string;
  textAlign: "left" | "center" | "right";
  fontWeight: "normal" | "bold";
  fontStyle: "normal" | "italic";
  textDecoration: "none" | "underline" | "line-through";
  x: number; // Position relative to canvas center
  y: number; // Position relative to canvas center
  rotation: number; // in degrees
  opacity: number; // 0-1
  effects?: Partial<EffectSettings>;
}

// Caption element for subtitles
export interface CaptionElement extends BaseTimelineElement {
  type: "caption";
  text: string;
  fontSize: number;
  fontFamily: string;
  color: string;
  backgroundColor: string;
  backgroundOpacity: number;
  position: "top" | "center" | "bottom";
  outline: boolean;
  outlineColor: string;
  outlineWidth: number;
  effects?: Partial<EffectSettings>;
}

// Sticker element for overlays
export interface StickerElement extends BaseTimelineElement {
  type: "sticker";
  stickerId: string;
  x: number; // Position relative to canvas center
  y: number; // Position relative to canvas center
  width: number;
  height: number;
  rotation: number; // in degrees
  opacity: number; // 0-1
  scale: number; // 0.1-5
  effects?: Partial<EffectSettings>;
}

// Typed timeline elements
export type TimelineElement = MediaElement | TextElement | CaptionElement | StickerElement;

// Creation types (without id, for addElementToTrack)
export type CreateMediaElement = Omit<MediaElement, "id">;
export type CreateTextElement = Omit<TextElement, "id">;
export type CreateCaptionElement = Omit<CaptionElement, "id">;
export type CreateStickerElement = Omit<StickerElement, "id">;
export type CreateTimelineElement = CreateMediaElement | CreateTextElement | CreateCaptionElement | CreateStickerElement;

export interface TimelineElementProps {
  element: TimelineElement;
  track: TimelineTrack;
  zoomLevel: number;
  isSelected: boolean;
  onElementMouseDown: (e: React.MouseEvent, element: TimelineElement) => void;
  onElementClick: (e: React.MouseEvent, element: TimelineElement) => void;
}

export interface ResizeState {
  elementId: string;
  side: "left" | "right";
  startX: number;
  initialTrimStart: number;
  initialTrimEnd: number;
}

// Drag data types for type-safe drag and drop
export interface MediaItemDragData {
  id: string;
  type: MediaType;
  name: string;
}

export interface TextItemDragData {
  id: string;
  type: "text";
  name: string;
  content: string;
}

export interface CaptionItemDragData {
  id: string;
  type: "caption";
  name: string;
  text: string;
}

export interface StickerItemDragData {
  id: string;
  type: "sticker";
  name: string;
  stickerId: string;
}

export type DragData = MediaItemDragData | TextItemDragData | CaptionItemDragData | StickerItemDragData;

export interface TimelineTrack {
  id: string;
  name: string;
  type: TrackType;
  elements: TimelineElement[];
  muted?: boolean;
  isMain?: boolean;
}

export function sortTracksByOrder(tracks: TimelineTrack[]): TimelineTrack[] {
  return [...tracks].sort((a, b) => {
    // Audio tracks always go to bottom
    if (a.type === "audio" && b.type !== "audio") return 1;
    if (b.type === "audio" && a.type !== "audio") return -1;

    // Main track goes above audio but below other tracks
    if (a.isMain && !b.isMain && b.type !== "audio") return 1;
    if (b.isMain && !a.isMain && a.type !== "audio") return -1;

    // Within same category, maintain creation order
    return 0;
  });
}

export function getMainTrack(tracks: TimelineTrack[]): TimelineTrack | null {
  return tracks.find((track) => track.isMain) || null;
}

export function ensureMainTrack(tracks: TimelineTrack[]): TimelineTrack[] {
  const hasMainTrack = tracks.some((track) => track.isMain);

  if (!hasMainTrack) {
    // Create main track if it doesn't exist
    const mainTrack: TimelineTrack = {
      id: generateUUID(),
      name: "Main Track",
      type: "media",
      elements: [],
      muted: false,
      isMain: true,
    };
    return [mainTrack, ...tracks];
  }

  return tracks;
}

// Timeline validation utilities
export function canElementGoOnTrack(
  elementType: "text" | "media" | "caption" | "sticker",
  trackType: TrackType
): boolean {
  if (elementType === "text") {
    return trackType === "text";
  } else if (elementType === "media") {
    return trackType === "media" || trackType === "audio";
  } else if (elementType === "caption") {
    return trackType === "caption";
  } else if (elementType === "sticker") {
    return trackType === "sticker";
  }
  return false;
}

export function validateElementTrackCompatibility(
  element: { type: "text" | "media" | "caption" | "sticker" },
  track: { type: TrackType }
): { isValid: boolean; errorMessage?: string } {
  const isValid = canElementGoOnTrack(element.type, track.type);

  if (!isValid) {
    let errorMessage = "";
    if (element.type === "text") {
      errorMessage = "Text elements can only be placed on text tracks";
    } else if (element.type === "media") {
      errorMessage = "Media elements can only be placed on media or audio tracks";
    } else if (element.type === "caption") {
      errorMessage = "Caption elements can only be placed on caption tracks";
    } else if (element.type === "sticker") {
      errorMessage = "Sticker elements can only be placed on sticker tracks";
    }

    return { isValid: false, errorMessage };
  }

  return { isValid: true };
}
